import { createI18n } from 'vue-i18n';
import en from './locales/en.json';
import fr from './locales/fr.json';
import it from './locales/it.json';
import de from './locales/de.json';
import es from './locales/es.json';

// Default = English. Locale persists in localStorage so the choice survives
// refreshes; on first visit we negotiate against `navigator.language` so an
// Italian browser opens in Italian without needing to toggle.
export const SUPPORTED_LOCALES = ['en', 'fr', 'it', 'de', 'es'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const STORAGE_KEY = 'timeoff_locale';

function detectLocale(): Locale {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && SUPPORTED_LOCALES.includes(saved as Locale)) return saved as Locale;
  const navLang = (navigator.language || 'en').slice(0, 2).toLowerCase();
  return SUPPORTED_LOCALES.includes(navLang as Locale) ? (navLang as Locale) : 'en';
}

export const i18n = createI18n({
  legacy: false,
  locale: detectLocale(),
  fallbackLocale: 'en',
  messages: { en, fr, it, de, es },
});

export function setLocale(loc: Locale): void {
  i18n.global.locale.value = loc;
  document.documentElement.lang = loc;
  localStorage.setItem(STORAGE_KEY, loc);
  // Persist to the server so server-side renders (notably outbound email
  // templates) address each user in their own language regardless of which
  // device or session triggered the action.
  void fetch('/api/me', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ preferredLocale: loc }),
  }).catch(() => { /* best effort — server falls back to 'fr' if missing */ });
}

// Apply the initial locale to the <html lang> attribute so screen readers
// pick up the right language and `:lang(xx)` CSS selectors work.
document.documentElement.lang = i18n.global.locale.value;
